import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { Calendar as CalendarIcon, Clock, PhoneCall, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Business {
  id: string;
  business_name: string;
  company_name: string;
  description: string;
}

const ScheduleCall = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('business');
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [timeSlot, setTimeSlot] = useState<string | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (businessId) {
      fetchBusinessDetails(businessId);
    }
  }, [businessId]);

  const fetchBusinessDetails = async (id: string) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('businesses')
        .select('id, business_name, company_name, description')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching business details:', error);
        toast({
          title: "Error",
          description: "Failed to load business details. Please try again.",
          variant: "destructive"
        });
        return;
      }

      setBusiness(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!date || !timeSlot) {
      toast({
        title: "Error",
        description: "Please select both date and time for the meeting.",
        variant: "destructive"
      });
      return;
    }

    const form = e.currentTarget;
    const formData = new FormData(form);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const phone = formData.get('phone') as string;
    const investmentAmount = formData.get('investmentAmount') as string;
    const topic = formData.get('topic') as string;

    if (!name || !email || !phone || !investmentAmount || !topic) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        toast({
          title: "Error",
          description: "Please log in to schedule a meeting.",
          variant: "destructive"
        });
        return;
      }

      const { data, error } = await supabase
        .from('meetings')
        .insert({
          business_id: businessId,
          investor_id: session.session.user.id,
          meeting_date: date.toISOString().split('T')[0],
          meeting_time: timeSlot,
          full_name: name,
          email,
          phone,
          investment_amount: parseFloat(investmentAmount),
          topic,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        console.error('Error scheduling meeting:', error);
        toast({
          title: "Error",
          description: "Failed to schedule meeting. Please try again.",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Meeting scheduled!",
        description: "We'll send you a confirmation email shortly.",
      });

      // Reset form
      form.reset();
      setDate(undefined);
      setTimeSlot(null);
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast({
        title: "Error",
        description: "Failed to schedule meeting. Please try again.",
        variant: "destructive"
      });
    }
  };

  const timeSlots = [
    "9:00 AM", "10:00 AM", "11:00 AM", 
    "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-block px-3 py-1 mb-4 rounded-full bg-purple-50 border border-purple-100">
                  <span className="text-sm font-medium text-h44s-purple">Schedule</span>
                </div>
                <h1 className="text-4xl font-bold mb-4">Schedule a Meeting</h1>
                {business ? (
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    Schedule a meeting to discuss investment opportunities with {business.business_name}.
                  </p>
                ) : (
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    Book a call with our investment advisors to discuss your financial goals 
                    and explore our opportunities.
                  </p>
                )}
              </div>
              
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <Tabs defaultValue="schedule">
                  <TabsList className="grid w-full grid-cols-2 mb-8">
                    <TabsTrigger value="schedule">Schedule a Meeting</TabsTrigger>
                    <TabsTrigger value="info">What to Expect</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="schedule">
                    <form onSubmit={handleSubmit}>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                          <h3 className="text-xl font-semibold mb-4 flex items-center">
                            <CalendarIcon className="mr-2 h-5 w-5 text-h44s-purple" />
                            Select a Date
                          </h3>
                          <div className="border rounded-lg p-4 bg-gray-50">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={setDate}
                              disabled={(date) => {
                                const day = date.getDay();
                                // Disable weekends
                                return day === 0 || day === 6 || date < new Date();
                              }}
                              className="mx-auto"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <h3 className="text-xl font-semibold mb-4 flex items-center">
                            <Clock className="mr-2 h-5 w-5 text-h44s-purple" />
                            Choose a Time
                          </h3>
                          
                          {date ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {timeSlots.map((slot) => (
                                <Button
                                  key={slot}
                                  type="button"
                                  variant={timeSlot === slot ? "default" : "outline"}
                                  className={`py-6 ${timeSlot === slot ? 'bg-h44s-purple hover:bg-h44s-purple/90' : ''}`}
                                  onClick={() => setTimeSlot(slot)}
                                >
                                  {slot}
                                </Button>
                              ))}
                            </div>
                          ) : (
                            <div className="border rounded-lg p-8 bg-gray-50 flex items-center justify-center text-gray-500 h-full">
                              Please select a date first
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {date && timeSlot && (
                        <div className="mt-8 pt-8 border-t">
                          <h3 className="text-xl font-semibold mb-4">Your Information</h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                              <Label htmlFor="name">Full Name</Label>
                              <Input id="name" name="name" placeholder="Your name" className="mt-1" required />
                            </div>
                            <div>
                              <Label htmlFor="email">Email Address</Label>
                              <Input id="email" name="email" type="email" placeholder="Your email" className="mt-1" required />
                            </div>
                            <div>
                              <Label htmlFor="phone">Phone Number</Label>
                              <Input id="phone" name="phone" type="tel" placeholder="Your phone number" className="mt-1" required />
                            </div>
                            <div>
                              <Label htmlFor="investmentAmount">Investment Amount (USD)</Label>
                              <Input id="investmentAmount" name="investmentAmount" type="number" placeholder="Estimated amount" className="mt-1" required />
                            </div>
                          </div>
                          
                          <div className="mb-6">
                            <Label htmlFor="topic">What would you like to discuss?</Label>
                            <Textarea 
                              id="topic" 
                              name="topic"
                              placeholder="Please provide a brief description of what you'd like to discuss in our meeting..." 
                              rows={3} 
                              className="mt-1"
                              defaultValue={business ? `I'm interested in discussing investment opportunities with ${business.business_name}.${business.description ? `\n\nBusiness Description: ${business.description}` : ''}` : ''}
                              required
                            />
                          </div>
                          
                          <Button type="submit" className="w-full bg-gradient-to-r from-h44s-blue to-h44s-purple text-lg py-6">
                            Schedule Meeting
                            <PhoneCall className="ml-2 h-5 w-5" />
                          </Button>
                        </div>
                      )}
                    </form>
                  </TabsContent>
                  
                  <TabsContent value="info">
                    <div className="space-y-8">
                      <Card>
                        <CardContent className="pt-6">
                          <h3 className="text-xl font-semibold mb-2">About the Meeting</h3>
                          <p className="text-gray-600">
                            {business ? (
                              <>Your scheduled meeting will be with representatives from {business.business_name} to discuss investment opportunities and answer any questions you may have. The meeting typically lasts 30-45 minutes.</>
                            ) : (
                              <>Your scheduled meeting will be with one of our investment advisors who will discuss your financial goals, risk tolerance, and investment preferences. The meeting typically lasts 30-45 minutes.</>
                            )}
                          </p>
                        </CardContent>
                      </Card>
                      
                      <Card>
                        <CardContent className="pt-6">
                          <h3 className="text-xl font-semibold mb-2">How to Prepare</h3>
                          <ul className="list-disc pl-5 text-gray-600 space-y-2">
                            <li>Have a general idea of your investment goals</li>
                            <li>Know your approximate investment amount</li>
                            <li>Prepare any specific questions you may have</li>
                            <li>Find a quiet place with good internet connection</li>
                          </ul>
                        </CardContent>
                      </Card>
                      
                      <Card>
                        <CardContent className="pt-6">
                          <h3 className="text-xl font-semibold mb-2">After the Meeting</h3>
                          <p className="text-gray-600">
                            We'll send you a summary of your discussion along with recommended next steps
                            and potential investment options tailored to your needs.
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ScheduleCall;
